import React, { useState, useEffect } from 'react';
import { getRevenueByCenter, getOccupancyTrend, getLeadSourcePerformance, getRenewalForecast, getVacancyHeatmap, logActivity } from '../../services/supabaseService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import RevenueChart from './components/RevenueChart';
import OccupancyTrendChart from './components/OccupancyTrendChart';
import LeadSourceChart from './components/LeadSourceChart';
import RenewalForecastChart from './components/RenewalForecastChart';
import VacancyHeatmap from './components/VacancyHeatmap';
import { analyzeRevenueTrends, analyzeLeadPerformance } from '../../services/aiAnalytics';
import toast from 'react-hot-toast';
import RevenueReconciliation from './components/RevenueReconciliation';

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [occupancyData, setOccupancyData] = useState([]);
  const [leadSourceData, setLeadSourceData] = useState([]);
  const [renewalData, setRenewalData] = useState([]);
  const [vacancyData, setVacancyData] = useState([]);
  const [activeTab, setActiveTab] = useState('revenue');
  const [aiInsights, setAiInsights] = useState({
    revenueAnalysis: null,
    leadAnalysis: null
  });
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    loadReportsData();
  }, []);

  useEffect(() => {
    if (error) {
      toast?.error(error?.message || 'Failed to load reports');
    }
  }, [error]);

  const loadReportsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [revenue, occupancy, leadSource, renewal, vacancy] = await Promise.all([
        getRevenueByCenter(),
        getOccupancyTrend(6),
        getLeadSourcePerformance(),
        getRenewalForecast(),
        getVacancyHeatmap()
      ]);

      setRevenueData(revenue);
      setOccupancyData(occupancy);
      setLeadSourceData(leadSource);
      setRenewalData(renewal);
      setVacancyData(vacancy);

      // Load AI insights
      loadAIInsights(revenue, leadSource);

      await logActivity({
        module: 'dashboard',
        action: 'export',
        entityType: 'reports',
        entityName: 'Reports Dashboard',
        description: 'Viewed reports dashboard'
      });
    } catch (err) {
      console.error('Reports load error:', err);
      setError(err?.message || 'Failed to load reports data');
    } finally {
      setLoading(false);
    }
  };

  const loadAIInsights = async (revenue, leads) => {
    try {
      setAiLoading(true);
      
      const [revenueAnalysis, leadAnalysis] = await Promise.all([
        analyzeRevenueTrends(revenue)?.catch(err => {
          console.error('Revenue analysis failed:', err);
          return null;
        }),
        analyzeLeadPerformance(leads)?.catch(err => {
          console.error('Lead analysis failed:', err);
          return null;
        })
      ]);

      setAiInsights({
        revenueAnalysis,
        leadAnalysis
      });
    } catch (err) {
      console.error('AI insights error:', err);
    } finally {
      setAiLoading(false);
    }
  };

  const exportReport = async () => {
    try {
      await logActivity({
        module: 'dashboard',
        action: 'export',
        entityType: 'reports',
        entityName: 'Reports Export',
        description: `Exported ${activeTab} report`
      });
      alert('Export functionality will be implemented with PDF generation');
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const tabs = [
    { id: 'revenue', label: 'Revenue by Center', icon: 'DollarSign' },
    { id: 'occupancy', label: 'Occupancy Trend', icon: 'TrendingUp' },
    { id: 'leads', label: 'Lead Performance', icon: 'Users' },
    { id: 'renewal', label: 'Renewal Forecast', icon: 'Calendar' },
    { id: 'vacancy', label: 'Vacancy Heatmap', icon: 'Map' }
  ];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading reports...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <p className="text-danger-800">Error: {error}</p>
          <Button onClick={loadReportsData} className="mt-4" variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Reports & Analytics</h1>
          <p className="text-muted-foreground flex items-center gap-2">
            Comprehensive insights and performance metrics
            {aiLoading && (
              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full flex items-center gap-1">
                <Icon name="Sparkles" className="w-3 h-3" />
                AI Analyzing...
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2 mt-4 md:mt-0">
          <Button onClick={loadReportsData} variant="outline" iconName="RefreshCw" iconPosition="left">
            Refresh
          </Button>
          <Button onClick={exportReport} variant="primary" iconName="Download" iconPosition="left">
            Export
          </Button>
        </div>
      </div>

      {/* Revenue Reconciliation Card */}
      <RevenueReconciliation />

      {/* Tabs */}
      <div className="mb-6 border-b border-border overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary-500 text-primary-600 font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span className="whitespace-nowrap">{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'revenue' && (
          <RevenueChart 
            data={revenueData} 
            aiAnalysis={aiInsights?.revenueAnalysis}
            loading={aiLoading}
          />
        )}
        {activeTab === 'occupancy' && <OccupancyTrendChart data={occupancyData} />}
        {activeTab === 'leads' && (
          <LeadSourceChart 
            data={leadSourceData} 
            aiAnalysis={aiInsights?.leadAnalysis}
            loading={aiLoading}
          />
        )}
        {activeTab === 'renewal' && <RenewalForecastChart data={renewalData} />}
        {activeTab === 'vacancy' && <VacancyHeatmap data={vacancyData} />}
      </div>
    </div>
  );
};

export default Reports;